import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BaseService } from '../../../base.service';
import { TestingToolkitService } from '../../../services/testing-toolkit.service';
import { Observable } from 'rxjs';

export interface PlanFeatureDetail {
  id: number;
  featureId: number;
  featureName: string;
  moduleName: string;
  isServiceItem: boolean;
  price: number;
  priceExtraStore: number;
  priceOneTime: number;
  dependencyId: number;
  transactionLimit: number;
}

export interface PlanModel {
  id: number;
  planName: string;
  planCode: string;
  planDetail: string;
  isActive: boolean;
  createdOnUTC: string;
  updatedOnUTC: string;
  details: PlanFeatureDetail[];
  isDemo?: boolean;
  transactionTitle?: string;
}

export interface LicenseFeatureEditRequest {
  dateEdit: string;
  detail: FeatureEditDetail[];
  licenseProduct_Id: number;
  planMaster_Id: number;
}

export interface FeatureEditDetail {
  feature_Id: number;
  isAddFeature: boolean;
}

export interface ApiResponse {
  apiStatus: {
    timeConsumed: string | null;
    isSuccess: boolean;
    message: string;
    details: string;
    reserved_A: number;
    obj: any;
    msgContainer: {
      isSuccss: boolean | null;
      message: string | null;
      details: string | null;
      timeConsumed: string | null;
      msgInfo_List: any[];
    };
  };
}

export interface FeatureComparison {
  featuresToAdd: PlanFeatureDetail[];
  featuresToRemove: PlanFeatureDetail[];
  isUpgrade: boolean;
  isDowngrade: boolean;
  currentPlan: PlanModel;
  targetPlan: PlanModel;
}

@Injectable({
  providedIn: 'root'
})
export class LicenseManagementService {
  constructor(
    private http: HttpClient,
    private BaseService: BaseService,
    private testingToolkit: TestingToolkitService
  ) { }

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('token');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
  }

  /**
   * Get all plans for a specific product (includes all details)
   */
  getPlansByProductId(productId: number): Observable<any> {
    const headers = this.getHeaders();
    return this.http.get(
      `${this.BaseService.BASEURL}/api/MS/Plan/GetByProductId`,
      {
        headers,
        params: {
          Id: productId.toString(),
        },
      }
    );
  }

  /**
   * Edit license features (Upgrade)
   */
  editLicenseFeatures(request: LicenseFeatureEditRequest): Observable<ApiResponse> {
    const headers = this.getHeaders();
    return this.http.post<ApiResponse>(
      `${this.BaseService.BASEURL}/api/License/License_EditFeatures`,
      request,
      {
        headers,
      }
    );
  }

  /**
   * Downgrade license plan
   */
  downGradeLicensePlan(licenseProductId: number, planMasterId: number): Observable<any> {
    const headers = this.getHeaders();
    const body = {
      licenseProduct_Id: licenseProductId,
      planMaster_Id: planMasterId
    };
    return this.http.post<any>(
      `${this.BaseService.BASEURL}/api/License/LicensePlan/DownGrade`,
      body,
      {
        headers,
      }
    );
  }

  /**
   * Map plan response to PlanModel with details
   */
  mapPlanResponse(plan: any): PlanModel | null {
    if (!plan) {
      return null;
    }

    const details = this.mapPlanDetails(plan?.planDetail || []);

    return {
      id: Number(plan?.id || 0),
      planName: plan?.planName || 'Plan',
      planCode: plan?.planCode || '',
      planDetail: plan?.planDetail || '',
      isActive: Boolean(plan?.isActive),
      createdOnUTC: plan?.createdOnUTC || '',
      updatedOnUTC: plan?.updatedOnUTC || '',
      details,
      isDemo: Boolean(plan?.isDemo || false),
      transactionTitle: plan?.transactionTitle || '',
    };
  }

  /**
   * Map plan details to feature array
   */
  private mapPlanDetails(planDetail: any[]): PlanFeatureDetail[] {
    if (!Array.isArray(planDetail)) {
      return [];
    }

    return planDetail
      .map((detail: any) => {
        const feature = detail?.productFeaturess || {};
        const moduleInfo = detail?.productModules || {};

        const featureId = Number(feature?.id ?? detail?.productFeatures_Id);
        if (!featureId) {
          return null;
        }

        return {
          id: Number(detail?.id ?? featureId),
          featureId,
          featureName: feature?.featureName || 'Unnamed Feature',
          moduleName: moduleInfo?.moduleName || 'General',
          isServiceItem: Boolean(feature?.isServiceItem),
          price: Number(feature?.price || 0),
          priceExtraStore: Number(feature?.price_ExtraStore || 0),
          priceOneTime: Number(feature?.price_OneTime || 0),
          dependencyId: Number(feature?.dependencyID || 0),
          transactionLimit: Number(detail?.transactionLimit || 0),
        } as PlanFeatureDetail;
      })
      .filter((detail): detail is PlanFeatureDetail => detail !== null);
  }

  /**
   * Compare features between current plan and target plan
   */
  compareFeatures(
    currentPlan: PlanModel,
    targetPlan: PlanModel
  ): FeatureComparison {
    const currentFeatureIds = currentPlan.details.map(f => f.featureId);
    const targetFeatureIds = targetPlan.details.map(f => f.featureId);

    // Find features to add (in target but not in current)
    const featuresToAdd = targetPlan.details.filter(
      f => !currentFeatureIds.includes(f.featureId)
    );

    // Find features to remove (in current but not in target)
    const featuresToRemove = currentPlan.details.filter(
      f => !targetFeatureIds.includes(f.featureId)
    );

    // Calculate total prices for upgrade/downgrade detection
    const currentTotal = currentPlan.details.reduce((sum, f) => sum + f.price, 0);
    const targetTotal = targetPlan.details.reduce((sum, f) => sum + f.price, 0);

    const isUpgrade = targetTotal > currentTotal;
    const isDowngrade = targetTotal < currentTotal;

    return {
      featuresToAdd,
      featuresToRemove,
      isUpgrade,
      isDowngrade,
      currentPlan,
      targetPlan
    };
  }

  /**
   * Build the request body for License_EditFeatures API
   */
  buildEditFeaturesRequest(
    comparison: FeatureComparison,
    licenseProductId: number,
    targetPlanId: number
  ): LicenseFeatureEditRequest {
    const details: FeatureEditDetail[] = [];

    // Add features from target plan that are not in current plan
    comparison.featuresToAdd.forEach(feature => {
      details.push({
        feature_Id: feature.featureId,
        isAddFeature: true
      });
    });

    // Remove features from current plan that are not in target plan
    comparison.featuresToRemove.forEach(feature => {
      details.push({
        feature_Id: feature.featureId,
        isAddFeature: false
      });
    });

    return {
      dateEdit: this.testingToolkit.getCurrentDateISO(),
      detail: details,
      licenseProduct_Id: licenseProductId,
      planMaster_Id: targetPlanId
    };
  }

  /**
   * Calculate total price for a plan
   * @param plan - The plan to calculate total for
   * @param license - Optional license object containing billing duration info (plD_Duration, plD_ChargeDuration)
   */
  getPlanTotal(plan: PlanModel, license?: any): number {
    if (!plan || !plan.details) {
      return 0;
    }

    // If license is provided, check if it's yearly billing
    let multiplier = 1;
    if (license && license.plD_Duration && license.plD_Duration !== 1) {
      // For yearly licenses, multiply by plD_ChargeDuration
      multiplier = license.plD_ChargeDuration || 1;
    }

    const extraStores = license?.extraStores ? Number(license.extraStores) : 0;

    return plan.details.reduce((sum, detail) => {
      let featureTotal = detail.price * multiplier;

      // Add extra store price if applicable
      if (extraStores > 0 && detail.priceExtraStore > 0) {
        featureTotal += (detail.priceExtraStore * extraStores * multiplier);
      }

      return sum + featureTotal;
    }, 0);
  }

  /**
   * Get billing frequency label for a plan
   * @param license - License object containing billing duration info
   */
  getBillingFrequency(license?: any): string {
    if (!license || !license.plD_Duration) {
      return '/month';
    }

    if (license.plD_Duration === 1) {
      return '/month';
    }

    return '/year';
  }
}

